import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Smile, Send, X, Loader2, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import CopyablePhone from './CopyablePhone';

/**
 * Reusable button that sends a WhatsApp template after showing a premium confirmation modal.
 * Props:
 * - templateId: string
 * - title: string (label for the button and modal)
 * - templateName: string (descriptive name in the modal)
 * - conversationId: optional conversation id
 * - phone: optional phone string
 * - icon: optional Lucide icon override
 * - onSent: callback({ wa_message_id, data })
 * - customAction: optional async function(templateId, phone, conversationId) => { tramite_id, ... }
 * - className: optional button class
 */
const IconMap = {
    '4362a743-0149-4ea6-b5b9-ee39f9363542': FileText, // Factura
    'a67a59f4-da9f-4138-b845-afb405b83388': Smile,    // Saludo
    'saludo_general': MessageSquare
};

const WhatsAppTemplateButton = ({ 
    templateId, 
    title, 
    templateName,
    conversationId, 
    phone, 
    icon: IconOverride,
    onSent, 
    onError,
    customAction,
    className 
}) => {
    const { authFetch } = useAuth();
    const { theme } = useTheme();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    
    const Icon = IconOverride || IconMap[templateId] || FileText;
    const displayName = templateName || title;

    const handleAction = async () => {
        setSending(true);
        try {
            let data = null;

            if (customAction) {
                // If a custom action is provided (e.g. for creating a tramite), use it
                console.log(`[WhatsAppTemplateButton] Executing custom action for ${templateId}`);
                data = await customAction(templateId, phone, conversationId);
            } else {
                // Default send-template logic
                let targetPhone = phone;
                if (!targetPhone && conversationId) {
                    const phRes = await authFetch(`/api/whatsapp/conversation-phone/${conversationId}`);
                    if (phRes.ok) {
                        const phData = await phRes.json();
                        targetPhone = phData.phone;
                    }
                }

                if (!targetPhone && !conversationId) {
                    alert('No se puede enviar el template: falta teléfono o conversación');
                    return;
                }

                const body = conversationId 
                    ? { conversation_id: conversationId, template_id: templateId } 
                    : { phone: targetPhone, template_id: templateId };

                const res = await authFetch('/api/whatsapp/send-template', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                });

                const result = await res.json().catch(() => ({}));
                const hubStatus = Number(result._hub_http_status);
                if (!res.ok || result.status === 'error' || hubStatus >= 400) {
                    const error = {
                        status: res.ok ? (hubStatus >= 400 ? hubStatus : 502) : res.status,
                        code: result.code,
                        message: result.message || result.error || result.details || result.detail || 'No se pudo enviar la plantilla'
                    };
                    if (onError) onError(error);
                    else alert(error.message);
                    return;
                }
                data = result;
            }

            if (data && onSent) {
                onSent(data);
            }
        } catch (err) {
            console.error('[WhatsAppTemplateButton] Error in action flow', err);
            if (onError) onError({ message: err.message });
            else alert(err.message || 'No se pudo enviar la plantilla');
        } finally {
            setSending(false);
            setConfirmOpen(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
                className={className}
                style={!className ? { 
                    padding: '0.5rem', 
                    borderRadius: '0.75rem', 
                    backgroundColor: 'hsl(var(--card))', 
                    color: 'hsl(var(--card-foreground))', 
                    borderColor: 'hsl(var(--border))', 
                    borderWidth: '1px',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                } : undefined}
                title={title}
            >
                <Icon size={18} />
                {title && (className?.includes('w-full') || className?.includes('px-4')) && (
                    <span className="text-xs font-bold uppercase tracking-widest">{title}</span>
                )}
            </button>

            {confirmOpen && createPortal(
                <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderWidth: '1px', filter: theme === 'light' ? 'invert(1) hue-rotate(180deg)' : 'none' }}>
                        {/* Header */}
                        <div className="p-6 border-b flex items-center justify-between" style={{ backgroundColor: 'hsl(var(--accent))', borderBottomColor: 'hsl(var(--border))' }}>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-500">
                                    <Icon size={20} />
                                </div>
                                <h3 className="font-bold tracking-tight" style={{ color: 'hsl(var(--card-foreground))' }}>{displayName}</h3>
                            </div>
                            <button
                                onClick={() => setConfirmOpen(false)}
                                className="p-2 hover:opacity-80 rounded-lg transition-colors"
                                style={{ color: 'hsl(var(--muted-foreground))' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            <p className="text-sm leading-relaxed" style={{ color: 'hsl(var(--foreground))' }}>
                                Estás a punto de enviar una plantilla de WhatsApp ({displayName}) {phone ? 'al número:' : 'a esta conversación.'}
                            </p>

                            {phone && (
                                <div className="flex items-center justify-center p-4 rounded-xl" style={{ backgroundColor: 'hsl(var(--accent))', borderColor: 'hsl(var(--border))', borderWidth: '1px' }}>
                                    <CopyablePhone
                                        phone={phone}
                                        textClassName="text-2xl font-mono font-bold text-emerald-500 tracking-widest"
                                    />
                                </div>
                            )}

                            <p className="text-[10px] text-center italic uppercase tracking-wider" style={{ color: 'hsl(var(--muted-foreground))' }}>
                                Confirma antes de continuar.
                            </p>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t flex gap-3" style={{ backgroundColor: 'hsl(var(--accent))', borderTopColor: 'hsl(var(--border))' }}>
                            <button
                                onClick={() => setConfirmOpen(false)}
                                className="flex-1 py-2.5 px-4 text-xs font-bold uppercase tracking-widest rounded-xl transition-colors hover:opacity-90"
                                style={{ backgroundColor: 'hsl(var(--secondary))', color: 'hsl(var(--secondary-foreground))' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAction}
                                disabled={sending}
                                className="flex-1 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        <span>Enviando...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Confirmar</span>
                                        <Send size={16} />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default WhatsAppTemplateButton;
